import { Component, computed, HostListener, inject, Input } from '@angular/core';
import { Store } from '@ngxs/store';
import { AgGridAngular } from 'ag-grid-angular';
import { TaxDataState } from '@app/app/store/tax-data/tax-data-state.service';
import {
  CellEditingStoppedEvent, CellValueChangedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import { BusinessState } from '@app/app/store/businesses/business.state';
import { FadeAnimationDirective } from '@app/app/animations/fade/fade-animation.directive';
import { fadeAnimation } from '@app/app/animations/fade/fade.animation';
import { DocumentPreviewComponent } from '@app/app/components/documents/document-preview/document-preview.component';
import { DscrDocumentService } from '../../dscr-document.service';
import { FinancesDebtComponent } from '../../finances-debt/finances-debt.component';
import { FinanceDefaultCol } from '../finance-view/finance-view-col-defs/finance-default.col';
import { FinanceAttributeIdCol } from '../finance-view/finance-view-col-defs/finance-attribute-id.col';
import { setFinanceAmountCol } from '../finance-view/finance-view-col-defs/finance-amount.col';
import { setFinanceAliasCol } from '../finance-view/finance-view-col-defs/finance-alias.col';
import { UpsertTaxDataValues } from '@app/app/store/tax-data/tax-data.actions';
import { DocumentIdCol } from '../finance-view/finance-view-col-defs/document-id.col';
import { FinanceViewGridOptions } from './finance-view-grid-options';

@Component({
  selector: 'app-finance-view',
  imports: [
    AgGridAngular,
    FinancesDebtComponent,
    DocumentPreviewComponent,
    FadeAnimationDirective
  ],
  templateUrl: './finance-view.component.html',
  styleUrls: ['./finance-view.component.scss'],
  animations: [fadeAnimation]
})
export class FinanceViewComponent {
  @Input({required: true}) financeContext: 'business' | 'personal' = 'business';
  private _store = inject(Store);
  private _dscrDocumentService = inject(DscrDocumentService);
  private _gridApi: GridApi | undefined;
  public defaultColDef: ColDef = FinanceDefaultCol;
  public gridOptions: GridOptions = FinanceViewGridOptions;
  public borrowerId = this._store.selectSignal(BusinessState.businessId);
  public taxValues = this._store.selectSignal(TaxDataState.taxDataValues);
  public documentId = computed(() => this._dscrDocumentService.documentId());
  public isDocumentVisible = computed(() => this._dscrDocumentService.isDocumentVisible());
  public shouldShowDocumentView = computed(() => {
    return this.isDocumentVisible() && this.documentId() > 0;
  })

  public contextTaxValues = computed(() => {
    return this.taxValues().filter(value => value.type === this.financeContext);
  })

  public targetYears = computed(() => {
    const taxYears = new Set<string>();
    this.contextTaxValues().forEach(value => taxYears.add(value.year.toString()));
    return Array.from(taxYears);
  });

  public colDefs = computed(() => {
    const financeAliasHeaderName = `${this.financeContext} finances`;
    const initialCols: ColDef[] = [
      FinanceAttributeIdCol,
      DocumentIdCol,
      setFinanceAliasCol(financeAliasHeaderName)
    ];
    const taxCols: ColDef[] = [];

    this.targetYears().forEach(year => {
      const col = setFinanceAmountCol(year);
      taxCols.push(col);
    });
    return [
      ...initialCols,
      ...taxCols.slice(0, 3)
    ]
  });

  public rowData = computed(() => {
    const groupedData = new Map();
    const yearKeys = this.targetYears().map(String);

    this.contextTaxValues().forEach(item => {
      const itemYearString = item.year.toString();

      if (yearKeys.includes(itemYearString)) {
        let row = groupedData.get(item.alias);

        if (!row) {
          row = {
            alias: item.alias,
            label: item.label,
            attributeId: item.attributeId,
            type: item.type,
            documentId: item.documentId,
          };

          yearKeys.forEach(yearKey => row[yearKey] = null);
          groupedData.set(item.alias, row);
        }

        row[itemYearString] = item.value;
      }
    });

    return Array.from(groupedData.values());
  });

  get isBusinessContext(): boolean {
    return this.financeContext === 'business';
  }

  onGridReady(event: GridReadyEvent): void {
    this._gridApi = event.api;
    event.api.sizeColumnsToFit();
  }

  handleCellValueChanged(event: CellValueChangedEvent): void {
    const newValue = event.newValue;
    const oldValue = event.oldValue;
    if (
      isNaN(newValue) ||
      newValue === null ||
      newValue === undefined ||
      newValue === '' ||
      newValue === oldValue
    ) {
      event.node.setDataValue(event.colDef.field!, event.oldValue);
    }
  }

  handleCellEditingStopped(event: CellEditingStoppedEvent): void {
    const newValue = event.newValue;
    const oldValue = event.oldValue;
    if (
        isNaN(newValue) ||
        newValue === null ||
        newValue === undefined ||
        newValue === '' ||
        newValue === oldValue
    ) {
      event.node.setDataValue(event.colDef.field!, event.oldValue);
    } else {
      const year = Number(event.colDef.headerName);
      const attributeId = event.data.attributeId;
      const value = event.newValue;
      const upsertPayload = [{year, attributeId, value}];

      this._store.dispatch(new UpsertTaxDataValues(this.borrowerId() as number, upsertPayload));
    }
  }

}
